import React, { Fragment, useCallback, useMemo } from 'react';
import { Alert, Linking, StyleSheet, TouchableOpacity, TouchableOpacityProps } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import ChevronRight from '@/assets/icons/chevronRightNew';
import { Badge } from '@/components/Badge';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Row } from '@/components/lib/components';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { trackRG } from '@/feature/analytics/trackRudderStackEvent';
import { usePickTypesEducation } from '@/feature/educational-picks/components/PickTypesEducationModalProvider';
import {
    DueDiligence,
    kycVerifiedSelector,
    useAuthUserConfig,
    userDueDiligenceSelector,
} from '@/hooks/use-auth-user-config';
import { showOneTrustPreferences } from '@/hooks/use-one-trust';
import { useUserStore } from '@/hooks/use-user-store';
import { navigateToKyc } from '@/navigation/navigation';
import { IdpvSource } from '@/navigation/types';
import { common } from '@/styles/styles';
import { ProductDocumentDataBodyScreenLinksSliceItem } from '@/types/prismic.generated';

import { AccountLink } from './AccountLink';

const Link = ({ label, children, style, ...props }: TouchableOpacityProps & { label?: string }) => {
    return (
        <TouchableOpacity activeOpacity={0.8} style={[styles.buttonMore, common.spaceBetweenRow, style]} {...props}>
            <Box flex={1} paddingVertical={'s16'}>
                <Text variant={'bodyLarge'} letterSpacing={-0.43} color={ds.colorToken.text.primary} lineHeight={24}>
                    {label}
                </Text>
            </Box>
            <Row alignItems="center" flexShrink={0}>
                {children}
                <ChevronRight style={styles.rightIcon} />
            </Row>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    buttonMore: {
        minHeight: 56,
        borderBottomWidth: 1,
        borderBottomColor: ds.colorValue.border.strong,
    },
    rightIcon: {
        marginLeft: 22,
    },
    noBorder: {
        borderBottomWidth: 0,
    },
});

/**
 * TODO:
 * ACCOUNT_LINKS should be moved to global state when we implement a CMS for this screens/links
 */
type Link = Partial<ProductDocumentDataBodyScreenLinksSliceItem>;

/* Account quick links */
export const Links = ({ links }: { links: Link[] }) => {
    const navigation = useNavigation();
    const profile = useUserStore(state => state.profile);
    const { data: isKycVerified } = useAuthUserConfig({ select: kycVerifiedSelector });
    const { data: userDueDiligence } = useAuthUserConfig({ select: userDueDiligenceSelector });

    const { launchIntercomMessenger } = useLaunchIntercomMessenger();
    const { showPickTypesEducationModal } = usePickTypesEducation();

    const isActiveLink = (link: Link) => typeof link.active === 'undefined' || link.active;
    const isNotValidForIdpv = (link: Link, isKyc: boolean, dueDiligence: DueDiligence | null) => {
        return link.screen === 'KycScanId' && !(isKyc && dueDiligence === null);
    };

    const activeLinks = useMemo(() => {
        if (!Array.isArray(links) || isKycVerified === undefined || userDueDiligence === undefined) {
            return [];
        }

        return links.filter(link => {
            const shouldHide = isNotValidForIdpv(link, isKycVerified, userDueDiligence);
            return !shouldHide && isActiveLink(link);
        });
    }, [links, isKycVerified, userDueDiligence]);

    const sectionNames = [...new Set(activeLinks?.map(item => item.section))];

    const handlePress = useCallback(
        async (link: Link) => {
            const label = link.label ?? '';
            const link_type = link.link_type ?? '';
            BetrAnalytics.trackProductEvent(AnalyticsEvent.ACCOUNT_SETTINGS, { label });
            trackRG.accountScreen(label);

            switch (true) {
                case !!link.screen:
                    if (link.screen === 'KycScanId') {
                        navigateToKyc(navigation, {
                            screen: 'KycScanId',
                            params: { source: IdpvSource.ACCOUNT_SETTING },
                        });
                        return;
                    }
                    if (link.screen === 'TrustlyId') {
                        navigateToKyc(navigation, { screen: 'TrustlyId' });
                        return;
                    }
                    navigation.navigate(link?.screen as never);
                    break;
                case link_type === 'PickTypesEducation':
                    showPickTypesEducationModal();
                    break;
                case link_type === 'SupportBetr':
                    launchIntercomMessenger();
                    break;
                case !!link.url:
                    const parsed = link.url?.replace('{userId}', profile.sub);
                    if (parsed) {
                        if (link.internal) {
                            navigation.navigate('ModalWebView', { uri: parsed });
                        } else if (await Linking.canOpenURL(parsed)) {
                            Linking.openURL(parsed);
                        }
                    }
                    break;
                case link_type === 'CookiePolicy':
                    showOneTrustPreferences();
                    break;
                default:
                    Alert.alert(label, 'Feature unavailable at this moment');
                    break;
            }
        },
        [launchIntercomMessenger, navigation, profile.sub, showPickTypesEducationModal]
    );

    if (!Array.isArray(activeLinks)) {
        return null;
    }

    return (
        <>
            {sectionNames.length > 0 &&
                sectionNames.map((sectionName, sectionIndex) => {
                    const filteredLinks = activeLinks?.filter(item => item.section === sectionName);

                    return (
                        <Fragment key={`section-${sectionIndex}-${sectionName}`}>
                            <SizedBox value={16} />

                            <AccountLink>
                                {filteredLinks
                                    ? filteredLinks.map((link: Link, linkIndex: number) => (
                                          <Link
                                              key={`link-${linkIndex}-${link.label}`}
                                              label={link.label ?? ''}
                                              onPress={() => handlePress(link)}
                                              style={[
                                                  linkIndex === filteredLinks.length - 1 ? styles.noBorder : undefined,
                                              ]}
                                          >
                                              {link?.badge_text ? <Badge label={link.badge_text} /> : null}
                                          </Link>
                                      ))
                                    : null}
                            </AccountLink>
                        </Fragment>
                    );
                })}
        </>
    );
};
